import React, { createContext, useContext, useCallback } from 'react';
import { authClient } from '../lib/auth';

const AUTH_URL =
  process.env.REACT_APP_AUTH_URL ??
  (process.env.NODE_ENV === 'production'
    ? 'https://auth.bikendure.com'
    : 'http://localhost:3001');

interface AuthUser {
  id: string;
  name: string;
  email: string;
  image?: string | null;
}

interface AuthContextValue {
  /** True once the session check has resolved (loaded or not). */
  isLoaded: boolean;
  /** True when a valid session exists. */
  isSignedIn: boolean;
  /** The current user, or null when not signed in. */
  user: AuthUser | null;
  /**
   * Returns a short-lived JWT suitable for Authorization: Bearer headers.
   * Fetches /api/auth/token using the current session cookie.
   */
  getToken: () => Promise<string | null>;
  /** Sign the current user out. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = authClient.useSession();

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(`${AUTH_URL}/api/auth/token`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      return (data as { token?: string }).token ?? null;
    } catch {
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    await authClient.signOut();
  }, []);

  const isLoaded = !isPending;
  const isSignedIn = isLoaded && session !== null && session !== undefined;
  const user: AuthUser | null = session?.user
    ? {
        id: session.user.id,
        name: session.user.name,
        email: session.user.email,
        image: session.user.image,
      }
    : null;

  return (
    <AuthContext.Provider value={{ isLoaded, isSignedIn, user, getToken, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
